import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { AccountService } from "@/services/AccountService"
import { MeterpointService } from "@/services/MeterpointService"
import { AgreementService } from "@/services/AgreementService"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { DAYS_YEAR } from "@/constants/general"
import tariffHelper from "@/helpers/tariffHelper"

export const getSuggestedPayment = (
  accountNumber: string,
  includeBalance: boolean,
  includeUnbilledReadings: boolean,
  specifiedMeterpoints?: IMeterpoint[],
) => {
  const queries = new QueryAggregator()

  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
  queries.add(coreIdQuery)

  const meterpointsQuery = MeterpointService.getForAccount(accountNumber)
  queries.add(meterpointsQuery)

  const meterpoints = specifiedMeterpoints ?? meterpointsQuery.data

  let annualCost = meterpoints.reduce((totalCost, meterpoint) => {
    const meterpointTariffsQuery = MeterpointService.getMeterpointTariffs(
      meterpoint.identifier,
      coreIdQuery.data,
    )
    queries.add(meterpointTariffsQuery)

    const meterpointAgreementsQuery = AgreementService.getForMeterpoint(
      coreIdQuery.data,
      meterpoint.identifier,
    )
    queries.add(meterpointAgreementsQuery)

    const tariff = meterpointTariffsQuery.data.currentTariff
    const registers = meterpoint.registers
    const activeAgreement = meterpointAgreementsQuery.data.find(
      (a) => a.isActive,
    )

    // Check if tariff in next month differs from current tariff
    if (activeAgreement && meterpoint.gsp) {
      const compareTariffsQuery =
        MeterpointService.getMonthlyTariffRateComparisons(
          meterpoint.gsp,
          activeAgreement.productCode,
        )

      queries.add(compareTariffsQuery)

      const nextTariffIsDifferent =
        !!compareTariffsQuery?.data.hasDifferentUnitRates
      const nextTariff = compareTariffsQuery?.data.nextMonth

      if (nextTariffIsDifferent && nextTariff && registers) {
        totalCost += tariffHelper.timePeriodCost({
          isGas: nextTariff.isGas,
          standingCharge: nextTariff.standingCharge,
          days: DAYS_YEAR,
          unitRates: nextTariff.unitRates,
          unitRate: nextTariff.unitRate,
          registers,
        })
        return totalCost
      }
    }

    if (tariff && registers) {
      totalCost += tariffHelper.annualCost(tariff, registers)
    }
    return totalCost
  }, 0)

  if (includeBalance) {
    const accountQuery = AccountService.getAccount(accountNumber)
    queries.add(accountQuery)
    annualCost += accountQuery.data.balance
  }

  if (includeUnbilledReadings) {
    const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
    queries.add(coreIdQuery)
    const unbilledCostsQuery = AccountService.getUnbilledReadingCosts(
      coreIdQuery.data,
    )
    queries.add(unbilledCostsQuery)
    annualCost += unbilledCostsQuery.data ?? 0
  }
  annualCost = Math.max(annualCost, 0)
  const suggestedPayment = Math.round(annualCost / 12)
  return queries.setData(suggestedPayment).getAggregate()
}

export type SuggestedPaymentBreakdown = {
  recommendedMonthlyPayment: number
  seasonalMonthlyPaymentSummer: number
  seasonalMonthlyPaymentWinter: number
  unbilledReadingsUsage?: number
}

export const getSuggestedPaymentBreakdown = (
  accountNumber: string,
  includeBalance: boolean,
  includeUnbilledReadings: boolean,
) => {
  const queries = new QueryAggregator()
  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)

  queries.add(coreIdQuery)

  let suggestedPaymentBreakdown: SuggestedPaymentBreakdown | null | undefined
  const coreId = coreIdQuery.data

  if (coreId) {
    const suggestedPaymentsQuery = AccountService.getSuggestedPayments(coreId)
    queries.add(suggestedPaymentsQuery)

    const suggestedPaymentData = suggestedPaymentsQuery.data
    if (includeUnbilledReadings && includeBalance) {
      suggestedPaymentBreakdown =
        suggestedPaymentData?.withUnbilled?.withBalance
    } else if (includeUnbilledReadings && !includeBalance) {
      suggestedPaymentBreakdown =
        suggestedPaymentData?.withUnbilled?.withoutBalance
    } else if (!includeUnbilledReadings && includeBalance) {
      suggestedPaymentBreakdown = suggestedPaymentData?.billedOnly?.withBalance
    } else if (!includeUnbilledReadings && !includeBalance) {
      suggestedPaymentBreakdown =
        suggestedPaymentData?.billedOnly?.withoutBalance
    }

    const unbilledRecommendedMonthlyPayment =
      suggestedPaymentData?.withUnbilled?.withBalance?.recommendedMonthlyPayment
    const billedRecommendedMonthlyPayment =
      suggestedPaymentData?.billedOnly?.withBalance?.recommendedMonthlyPayment

    if (
      suggestedPaymentBreakdown &&
      unbilledRecommendedMonthlyPayment &&
      billedRecommendedMonthlyPayment
    ) {
      const unbilledReadingsUsage = Number(
        (
          (unbilledRecommendedMonthlyPayment -
            billedRecommendedMonthlyPayment) *
          12
        ).toFixed(2),
      )

      suggestedPaymentBreakdown = {
        ...suggestedPaymentBreakdown,
        unbilledReadingsUsage: unbilledReadingsUsage,
      }
    }
  }
  return queries.setData(suggestedPaymentBreakdown).getAggregate()
}
